import type { ReactNode } from "react";

import { useUser } from "@/contexts/UserContext";
import { useWorkspaceMembers } from "./use-workspace-members";

/**
 * True when at least one workspace member reports to the current user.
 *
 * The deal alert views are scoped to the user's own deals PLUS their reports' — see
 * `isAlertDealListId` in DealsPage, which widens `scopedOwnerIds` with `directReportIds`. So
 * a manager reading "these deals of yours" is being told something untrue about what the CTA
 * will show them, and the copy has to know which of the two it is.
 *
 * ⚠️ `useWorkspaceMembers` fetches the whole member list on mount, with no skip option. Do
 * NOT call this from an always-mounted component (`docs/COMMON_BUGS.md` — the notification
 * bell lives in AppLayout and would fetch members on every page load, for a string). Use
 * `HasDirectReports` below, which only mounts inside the open popover.
 */
export function useHasDirectReports(): boolean {
  const { members } = useWorkspaceMembers();
  const { currentUser } = useUser();
  const myId = currentUser?.id;
  if (!myId) return false;
  return members.some(m => m.reportsTo === myId);
}

/**
 * Render-prop wrapper, so an always-mounted parent only fetches members while this subtree
 * is mounted — React runs no hooks for a component that is not rendered.
 *
 * Mount it ONCE around a list rather than per row: the hook is one fetch per call, so
 * calling it inside each row would issue a request per notification.
 */
export function HasDirectReports({ children }: { children: (hasReports: boolean) => ReactNode }) {
  const hasReports = useHasDirectReports();
  return <>{children(hasReports)}</>;
}
